import React, { useState } from "react";
import { Modal, Button, Spin, Tag, Empty, Collapse, Tooltip } from "antd";
import {
  FileSearchOutlined,
  ReloadOutlined,
  FileTextOutlined,
  CopyOutlined,
  RobotOutlined,
  CheckCircleOutlined,
  ReadOutlined,
  SearchOutlined,
  RightOutlined,
} from "@ant-design/icons";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";
import { Severty, ShowToast } from "../../helper/toast";

const { Panel } = Collapse;

const panelStyle = {
  marginBottom: 10,
  background: "#fff",
  borderRadius: 10,
  border: "1px solid #f0f0f0",
  overflow: "hidden",
};

// Small colored icon badge + title + at-a-glance value, reused as every
// report section's Collapse header — same icon/color per check as the
// inline stat chips, so a section is recognizable before it's even
// expanded, and the headline number is visible without opening it.
function panelHeader({ icon, color, bg, title, badge }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, width: "100%" }}>
      <span
        style={{
          width: 28,
          height: 28,
          borderRadius: 8,
          background: bg,
          color,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 14,
          flexShrink: 0,
        }}
      >
        {icon}
      </span>
      <span style={{ fontWeight: 600, fontSize: 14 }}>{title}</span>
      {badge && (
        <Tag color={badge.color} style={{ marginLeft: "auto", marginRight: 8 }}>
          {badge.text}
        </Tag>
      )}
    </div>
  );
}

function stat({ icon, color, bg, label, value }) {
  return (
    <Tooltip title={label}>
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          height: 34,
          padding: "0 12px",
          borderRadius: 999,
          background: bg,
          color,
          fontSize: 13,
          fontWeight: 600,
          whiteSpace: "nowrap",
          cursor: "default",
        }}
      >
        {icon}
        {value}
      </span>
    </Tooltip>
  );
}

// Staff's inline Analyze result strip — lives right inside the version row
// it belongs to, after its eye/editor/download icons, using that row's own
// free horizontal space instead of a popup. Once a version has been
// scanned, the result is part of its own document (scanResult, cached
// server-side by BlogWriteService.analyzeVersion) and comes back with the
// version list itself — so it just renders here permanently on every visit,
// no re-click needed. Only an unscanned version shows the small trigger
// icon in its place. Whichever side (writer or reviewer) scans first, the
// cached numbers show up for the other side too.
function VersionAnalyzeModal({ version, requestId, onAnalyzed }) {
  const { request } = useRequest();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [localResult, setLocalResult] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);

  const result = version?.scanResult?.scannedAt ? version.scanResult : localResult;

  const runScan = () => {
    setLoading(true);
    setError(null);
    request({
      url: apiPath.blogWriteRequestVersionAnalyze(requestId, version._id),
      method: "POST",
      onSuccess: (data) => {
        setLoading(false);
        if (data.status === 200) {
          setLocalResult(data.data.result);
          onAnalyzed?.(version._id, data.data.result);
        } else {
          setError(data.message || "Analysis failed");
          ShowToast(data.message || "Analysis failed", Severty.ERROR);
        }
      },
      onError: () => {
        setLoading(false);
        setError("Analysis failed");
        ShowToast("Analysis failed", Severty.ERROR);
      },
    });
  };

  if (!result) {
    if (loading) {
      return (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, color: "#888" }}>
          <Spin size="small" /> Analyzing…
        </span>
      );
    }
    if (error) {
      return (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 13, color: "#dc2626" }}>{error}</span>
          <Button size="small" icon={<ReloadOutlined />} onClick={runScan}>
            Retry
          </Button>
        </span>
      );
    }
    return (
      <a className="gfx-version-inline-dl" href="#" onClick={(e) => { e.preventDefault(); runScan(); }} title="Analyze">
        <FileSearchOutlined />
      </a>
    );
  }

  const aiScore = result.aiScore;
  const originalityScore = result.plagiarismScore != null ? Math.round(100 - result.plagiarismScore) : null;

  return (
    <>
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        {stat({
          icon: <FileTextOutlined />,
          color: "#595959",
          bg: "#f0f0f0",
          label: "Readability Score",
          value: result.readability?.label ? `${result.readability.score}/10` : "N/A",
        })}
        {stat({
          icon: <CopyOutlined />,
          color: "#dc2626",
          bg: "#fef2f2",
          label: "Plagiarism (Originality)",
          value: originalityScore != null ? `${originalityScore}% Original` : "N/A",
        })}
        {stat({
          icon: <RobotOutlined />,
          color: "#7c3aed",
          bg: "#f5f3ff",
          label: "AI Detection",
          value: aiScore != null ? `${Math.round(aiScore)}% AI` : "N/A",
        })}
        {stat({
          icon: <CheckCircleOutlined />,
          color: "#16a34a",
          bg: "#f0fdf4",
          label: "Grammar & Spelling",
          value: result.grammar?.grade || "N/A",
        })}
        {stat({
          icon: <ReadOutlined />,
          color: "#2563eb",
          bg: "#eff6ff",
          label: "Content Quality",
          value: result.contentQuality?.label || "N/A",
        })}
        {stat({
          icon: <SearchOutlined />,
          color: "#d97706",
          bg: "#fffbeb",
          label: "Fact Check",
          value: `${result.facts?.length || 0} claim(s)`,
        })}
        <Button size="small" type="link" onClick={() => setReportOpen(true)} style={{ padding: "0 4px" }}>
          View Full Report
        </Button>
      </span>

      <Modal
        open={reportOpen}
        onCancel={() => setReportOpen(false)}
        footer={null}
        width={700}
        title={
          <div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>Full Analysis Report</div>
            <div style={{ fontSize: 12, fontWeight: 400, color: "#999", marginTop: 2 }}>
              {version?.name || `Version ${version?.version}`}
            </div>
          </div>
        }
        bodyStyle={{ background: "#fafafa", padding: 16 }}
      >
        <Collapse
          defaultActiveKey={["plagiarism"]}
          bordered={false}
          ghost
          expandIcon={({ isActive }) => (
            <RightOutlined style={{ fontSize: 11, color: "#999", transition: "transform 0.2s", transform: isActive ? "rotate(90deg)" : "none" }} />
          )}
        >
          <Panel
            header={panelHeader({
              icon: <CopyOutlined />,
              color: "#dc2626",
              bg: "#fef2f2",
              title: "Plagiarism",
              badge: originalityScore != null ? { color: "red", text: `${result.plagiarismMatches?.length || 0} match(es)` } : null,
            })}
            key="plagiarism"
            style={panelStyle}
          >
            {!result.plagiarismMatches?.length ? (
              <Empty description="No matched sources found" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: "12px 0" }} />
            ) : (
              result.plagiarismMatches.map((m, i) => (
                <div key={i} style={{ marginBottom: 14, paddingBottom: 14, borderBottom: "1px solid #f0f0f0" }}>
                  <p style={{ marginBottom: 4 }}>"{m.text}"</p>
                  <a href={m.url} target="_blank" rel="noreferrer" style={{ fontSize: 13 }}>
                    {m.url}
                  </a>
                  <Tag color="orange" style={{ marginLeft: 8 }}>{m.similarity}% match</Tag>
                </div>
              ))
            )}
          </Panel>

          <Panel
            header={panelHeader({
              icon: <RobotOutlined />,
              color: "#7c3aed",
              bg: "#f5f3ff",
              title: "AI Detection",
              badge: aiScore != null ? { color: aiScore >= 50 ? "purple" : "green", text: `${Math.round(aiScore)}% AI` } : null,
            })}
            key="ai"
            style={panelStyle}
          >
            {aiScore != null ? (
              <p>
                <strong>{result.aiVerdict}</strong> — {Math.round(aiScore)}% likelihood of AI-generated content.
              </p>
            ) : (
              <Empty description="No AI detection data" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: "12px 0" }} />
            )}
          </Panel>

          <Panel
            header={panelHeader({
              icon: <FileTextOutlined />,
              color: "#595959",
              bg: "#f0f0f0",
              title: "Readability",
              badge: result.readability?.label ? { color: "default", text: result.readability.label } : null,
            })}
            key="readability"
            style={panelStyle}
          >
            {result.readability?.label ? (
              <>
                <p>
                  <strong>{result.readability.label}</strong> — score{" "}
                  {result.readability.score}/10
                </p>
                <p style={{ color: "#666", marginBottom: 14 }}>{result.readability.description}</p>
                {result.readabilityDetails?.metrics?.length > 0 && (
                  <div>
                    {result.readabilityDetails.metrics.map((m) => (
                      <div
                        key={m.key}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "8px 0",
                          borderBottom: "1px solid #f0f0f0",
                          gap: 12,
                        }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>{m.label}</div>
                          <div style={{ color: "#999", fontSize: 12 }}>{m.hint}</div>
                        </div>
                        <div style={{ textAlign: "right", flexShrink: 0 }}>
                          <Tag color={m.good == null ? "default" : m.good ? "green" : "orange"}>{m.value}</Tag>
                          {m.ideal && <div style={{ color: "#999", fontSize: 11, marginTop: 2 }}>Ideal: {m.ideal}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <Empty description="No readability data" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: "12px 0" }} />
            )}
          </Panel>

          <Panel
            header={panelHeader({
              icon: <CheckCircleOutlined />,
              color: "#16a34a",
              bg: "#f0fdf4",
              title: "Grammar & Spelling",
              badge: result.grammar?.grade ? { color: "green", text: `Grade ${result.grammar.grade}` } : null,
            })}
            key="grammar"
            style={panelStyle}
          >
            {result.grammar?.score != null ? (
              <>
                <p>
                  Grade <strong>{result.grammar.grade}</strong> ({result.grammar.score}%),{" "}
                  {result.grammar.issues?.length || 0} issue(s) found
                </p>
                {result.grammar.issues?.slice(0, 15).map((issue, i) => (
                  <div key={i} style={{ marginBottom: 10 }}>
                    <p style={{ marginBottom: 2 }}>{issue.message}</p>
                    {issue.replacements?.length > 0 && (
                      <p style={{ color: "#666", fontSize: 13 }}>
                        Suggestion: {issue.replacements.join(", ")}
                      </p>
                    )}
                  </div>
                ))}
              </>
            ) : (
              <Empty description="No grammar data" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: "12px 0" }} />
            )}
          </Panel>

          <Panel
            header={panelHeader({
              icon: <ReadOutlined />,
              color: "#2563eb",
              bg: "#eff6ff",
              title: "Content Quality",
              badge: result.contentQuality?.label ? { color: "blue", text: result.contentQuality.label } : null,
            })}
            key="contentQuality"
            style={panelStyle}
          >
            {result.contentQuality?.label ? (
              <>
                <p>
                  <strong>{result.contentQuality.label}</strong> — {result.contentQuality.confidence}% confidence
                </p>
                {result.contentQuality.probs && (
                  <div style={{ color: "#666", fontSize: 13 }}>
                    {Object.entries(result.contentQuality.probs).map(([band, pct]) => (
                      <div key={band}>
                        {band}: {pct}%
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <Empty description="No content quality data" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: "12px 0" }} />
            )}
          </Panel>

          <Panel
            header={panelHeader({
              icon: <SearchOutlined />,
              color: "#d97706",
              bg: "#fffbeb",
              title: "Fact Check",
              badge: { color: "gold", text: `${result.facts?.length || 0} claim(s)` },
            })}
            key="facts"
            style={panelStyle}
          >
            {!result.facts?.length ? (
              <Empty description="No fact-checkable claims found" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: "12px 0" }} />
            ) : (
              result.facts.map((f, i) => (
                <div key={i} style={{ marginBottom: 12 }}>
                  <p style={{ marginBottom: 2 }}>{f.claim}</p>
                  <Tag
                    color={
                      f.verdict === "Supported" ? "green" : f.verdict === "Contradicted" ? "red" : "orange"
                    }
                  >
                    {f.verdict}
                  </Tag>
                  {f.explanation && <p style={{ color: "#666", fontSize: 13, marginTop: 4 }}>{f.explanation}</p>}
                </div>
              ))
            )}
          </Panel>
        </Collapse>
      </Modal>
    </>
  );
}

export default VersionAnalyzeModal;
